import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Split } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { fmtDate, fmtEuro, isoDate } from "@/lib/format";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { COST_KINDS, FormDialog, opts } from "@/components/fleet/fleetConst";

const Allocation = ({ month, can }) => {
  const [res, setRes] = useState(null);
  useEffect(() => {
    api.get("/fleet/allocations").then((r) => setRes(r.data.find((a) => a.month === month) || null)).catch(() => {});
  }, [month]);
  const run = async () => {
    try { const { data } = await api.post("/fleet/allocate", { month }); setRes(data); toast.success("Costi ripartiti sugli interventi"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-4 flex flex-wrap items-center gap-4 border-l-4 border-primary bg-white px-4 py-3 text-sm" data-testid="allocation-box">
      <div className="flex-1">
        <div className="font-semibold">Ripartizione sugli interventi della squadra</div>
        {res ? <div className="text-muted-foreground" data-testid="allocation-result">Ripartiti <strong>{fmtEuro(res.allocated)}</strong> su {res.orders} interventi · non ripartiti <strong>{fmtEuro(res.unallocated)}</strong> (mezzi senza squadra o senza interventi nel mese)</div>
          : <div className="text-muted-foreground">Non ancora calcolata per il mese. Il calcolo automatico gira ogni mattina alle 7:00.</div>}
      </div>
      {can && <Button variant="outline" onClick={run} data-testid="allocation-run-button"><Split className="mr-1 h-4 w-4" />Ripartisci ora</Button>}
    </div>
  );
};

export const CostsTab = ({ vehicles, refreshKey, onChanged }) => {
  const { can } = useAuth();
  const [month, setMonth] = useState(isoDate(new Date()).slice(0, 7));
  const { inCompany } = useData();
  const [raw, setRows] = useState([]);
  const rows = raw.filter((r) => inCompany(r.company_id));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = useCallback(() => api.get("/fleet/costs", { params: { month } }).then((r) => setRows(r.data)).catch(() => {}), [month]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const save = async () => {
    try { await api.post("/fleet/costs", { ...form, km: form.km || null }); toast.success("Costo registrato"); setOpen(false); load(); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (c) => {
    if (!window.confirm("Eliminare il costo?")) return;
    try { await api.delete(`/fleet/costs/${c.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const fields = [
    { key: "vehicle_id", label: "Mezzo", type: "select", options: vehicles.map((v) => ({ value: v.id, label: `${v.plate} · ${v.model}` })), wide: true },
    { key: "date", label: "Data", type: "date" }, { key: "kind", label: "Tipo", type: "select", options: opts(COST_KINDS) },
    { key: "amount", label: "Importo (€)", type: "number" }, { key: "km", label: "Km (facoltativo)", type: "number" }, { key: "description", label: "Descrizione", wide: true },
  ];
  const total = rows.reduce((s, c) => s + c.amount, 0);
  return (
    <div data-testid="costs-tab">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input type="month" className="w-48 bg-white" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="costs-month" />
        <span className="text-sm">Totale mese: <strong className="font-mono" data-testid="costs-total">{fmtEuro(total)}</strong></span>
        {can("fleet:write") && <Button className="ml-auto" onClick={() => { setForm({ vehicle_id: "", date: isoDate(new Date()), kind: "FUEL", amount: 0, km: 0, description: "" }); setOpen(true); }} data-testid="cost-add-button"><Plus className="mr-1 h-4 w-4" />Registra costo</Button>}
      </div>
      <Allocation month={month} can={can("fleet:write")} key={`${month}${refreshKey}`} />
      {rows.length === 0 ? <Empty text="Nessun costo nel mese" testId="costs-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Data</TableHead><TableHead>Mezzo</TableHead><TableHead>Tipo</TableHead><TableHead>Descrizione</TableHead><TableHead className="text-right">Importo</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} data-testid={`cost-row-${c.id}`}>
                  <TableCell className="font-mono text-xs">{fmtDate(c.date)}</TableCell>
                  <TableCell className="font-mono text-sm font-bold">{c.plate}</TableCell>
                  <TableCell className="text-sm">{COST_KINDS[c.kind]}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.description}</TableCell>
                  <TableCell className="text-right font-mono">{fmtEuro(c.amount)}</TableCell>
                  <TableCell>{can("fleet:write") && !c.maintenance_id && <Button size="icon" variant="ghost" onClick={() => remove(c)} data-testid={`cost-delete-${c.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <FormDialog open={open} onClose={() => setOpen(false)} title="Registra costo mezzo" fields={fields} form={form} setForm={setForm} onSave={save} testPrefix="cost" />
    </div>
  );
};
